import type { ContactNumber } from '@hue/contracts';
import type { PortalPageSettings, SocialLink } from '@hue/contracts';
import { api } from './api.js';
export interface PortalPublication {
  page?: PortalPageSettings | undefined;
  published: boolean;
  description: string;
  unitIds: string[];
  serviceIds: string[];
  requireRoom: boolean;
}
export interface PortalCompany {
  contactNumbers?: ContactNumber[];
  page?: PortalPageSettings | undefined;
  socialLinks?: SocialLink[];
  logoIds?: { main: string | null; dark: string | null; symbol?: string | null };
  key: string;
  displayName: string;
  description: string;
  units: Array<{
    id: string;
    displayName: string;
    city: string | null;
    state: string | null;
    street: string | null;
    streetNumber: string | null;
    district: string | null;
    phone: string | null;
    email: string | null;
    timeZone: string;
    allowProfessionalChoice: boolean;
    socialLinks?: SocialLink[];
    contactNumbers?: ContactNumber[];
    openingHours: Array<{ weekday: number; startMinute: number; endMinute: number }>;
  }>;
  services: Array<{
    id: string;
    displayName: string;
    description: string | null;
    durationMinutes: number;
    priceCents: number;
    currency: string;
    /// Preço e duração em cada unidade publicada (ADR 0074).
    units?: Array<{
      businessUnitId: string;
      durationMinutes: number;
      priceCents: number;
      /// A faixa entre os profissionais que realizam o serviço na unidade.
      minPriceCents?: number;
      maxPriceCents?: number;
      minDurationMinutes?: number;
      maxDurationMinutes?: number;
    }>;
  }>;
}

export interface PortalAvailability {
  unavailabilityReason?: 'DEPOSIT_UNAVAILABLE' | null;
  /// A frase da política de cancelamento do serviço na unidade, lida antes de pedir (ADR 0071).
  cancellationSummary?: string;
  date: string;
  timeZone: string;
  slots: Array<{
    startsAt: string;
    endsAt: string;
    startTime: string;
    endTime: string;
    /// O preço e a duração de quem atende neste horário (ADR 0074, decisão 12).
    durationMinutes?: number;
    priceCents?: number;
    deposit: { amountCents: number; dueAt: string | null; expiryAction: string };
  }>;
  professionals: Array<{ id: string; displayName: string }>;
  requiresConfirmation: true;
}
export interface PortalAppointment {
  /// A empresa do pedido: a área do cliente junta todas (ADR 0067).
  companyKey: string;
  companyName: string;
  serviceId: string;
  portalOrigin: boolean;
  id: string;
  businessUnitId: string;
  serviceDisplayName: string;
  startsAt: string;
  endsAt: string;
  timeZone: string;
  status: 'SCHEDULED' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
  priceCents: number;
  currency: string;
  requiresConfirmation: boolean;
  queuePosition: number | null;
  depositAmountCents: number;
  depositDueAt: string | null;
  depositPaidCents: number;
}
export interface PortalFinancial {
  policy: {
    policyVersion: string;
    /// Antecedência recomendada aceita nos pedidos anteriores ao ADR 0071; nula nos novos.
    acceptedNoticeHours: number | null;
    acceptedAt: string;
    text: { cancellation?: string; confirmation: string; credit: string } | null;
    /// A regra de cancelamento retratada no pedido; nula nos anteriores ao ADR 0071.
    cancellationSummary?: string | null;
  } | null;
  cancellation: {
    id: string;
    disposition: 'REFUND' | 'CREDIT';
    status: 'PENDING' | 'DONE' | 'REQUIRES_ACTION';
    error: string | null;
    createdAt: string;
    /// O que a política reteve; zero sem retenção (ADR 0071).
    retainedCents?: number;
    /// O que volta ao cliente; nulo quando não houve retenção.
    returnedCents?: number | null;
  } | null;
  credit: Array<{
    currency: string;
    amountCents: number;
    availableCents: number;
    businessUnitId: string;
  }>;
  creditScope: string;
  charges: Array<{
    id: string;
    amountCents: number;
    status: string;
    verificationPending?: boolean;
    refundStatus: string | null;
    paymentReceiptUrl: string | null;
    refundReceiptUrl: string | null;
    invoiceUrl: string | null;
  }>;
}
/// A prévia do cancelamento pelo cliente: o valor exato retido antes de confirmar (ADR 0071).
export interface PortalCancellationPreview {
  currency: string;
  paidCents: number;
  retainedCents: number;
  returnedCents: number;
  /// O destino do que volta; `CHOICE` deixa o cliente escolher.
  remainder: 'CHOICE' | 'REFUND' | 'CREDIT';
  withinHours: number | null;
  summary: string | null;
}
export interface PortalPayment {
  paymentMethod: 'PIX' | 'CARD' | null;
  id: string;
  status: string;
  amountCents: number;
  invoiceUrl: string | null;
  pixPayload: string | null;
  pixQrImage: string | null;
  environment: string;
}
/// O endereço de um pedido da pessoa numa empresa (ADR 0067): a empresa vai no caminho.
function appointmentPath(companyKey: string, appointmentId: string, action: string) {
  return `/customer/portal/companies/${encodeURIComponent(companyKey)}/appointments/${encodeURIComponent(appointmentId)}/${action}`;
}
export interface PortalPrepaidSession {
  id: string;
  packageName: string;
  saleNumber: number;
  available: number;
  expiresOn: string | null;
  releasedOn: string | null;
  businessUnitName: string;
}
export const portalApi = {
  prepaidBalances: async (
    key: string,
    params: {
      businessUnitId: string;
      serviceId: string;
      date?: string | undefined;
      search: string;
      limit: number;
      offset: number;
    },
  ) =>
    (
      await api.get<{ balances: PortalPrepaidSession[]; total: number }>(
        `/customer/portal/companies/${encodeURIComponent(key)}/prepaid-balances`,
        { params },
      )
    ).data,

  companyServices: async (
    key: string,
    params: { unitId: string; search: string; limit: number; offset: number },
  ) =>
    (
      await api.get<{ services: PortalCompany['services']; total: number }>(
        `/public/portal/companies/${encodeURIComponent(key)}/services`,
        { params },
      )
    ).data,
  preview: async (publication: PortalPublication) =>
    (await api.post<{ company: PortalCompany }>('/tenant/scheduling/portal/preview', publication))
      .data.company,
  mediaUrl: (companyKey: string, id: string, privatePreview = false, logo = false) =>
    `${api.defaults.baseURL ?? ''}${privatePreview ? (logo ? '/tenant/brand/files/' : '/tenant/scheduling/portal/media/') + encodeURIComponent(id) : '/public/portal/companies/' + encodeURIComponent(companyKey) + '/media/' + encodeURIComponent(id)}`,
  uploadMedia: async (file: File) => {
    const content = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]!);
      reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
      reader.readAsDataURL(file);
    });
    return (
      await api.post<{ id: string; scanned: boolean }>('/tenant/scheduling/portal/media', {
        fileName: file.name,
        content,
      })
    ).data;
  },
  saveBilling: async (companyKey: string, document: string) => {
    await api.put(`/customer/portal/companies/${encodeURIComponent(companyKey)}/billing`, {
      document,
    });
  },
  pay: async (companyKey: string, appointmentId: string, method: 'PIX' | 'CARD') =>
    (
      await api.post<{ charge: PortalPayment }>(
        appointmentPath(companyKey, appointmentId, 'payment'),
        { method },
      )
    ).data.charge,
  financial: async (companyKey: string, appointmentId: string) =>
    (await api.get<PortalFinancial>(appointmentPath(companyKey, appointmentId, 'financial'))).data,
  cancellationPreview: async (companyKey: string, appointmentId: string) =>
    (
      await api.get<PortalCancellationPreview>(
        appointmentPath(companyKey, appointmentId, 'cancellation'),
      )
    ).data,
  cancel: async (companyKey: string, appointmentId: string, disposition: 'REFUND' | 'CREDIT') =>
    (
      await api.post<{ cancelled: true; financial: PortalFinancial }>(
        appointmentPath(companyKey, appointmentId, 'cancellation'),
        { disposition },
      )
    ).data,
  linked: async (companyKey: string) =>
    (
      await api.get<{ linked: boolean }>(
        `/customer/portal/companies/${encodeURIComponent(companyKey)}/link`,
      )
    ).data.linked,
  reschedule: async (
    companyKey: string,
    appointmentId: string,
    input: {
      date: string;
      startTime: string;
      professionalId?: string | undefined;
      reuseDeposit: true;
    },
  ) => {
    await api.post(appointmentPath(companyKey, appointmentId, 'reschedule'), input);
  },

  policy: async () =>
    (
      await api.get<{
        version: string;
        confirmation: string;
        credit: string;
      }>('/public/portal/policy')
    ).data,
  appointments: async (params: {
    limit: number;
    offset: number;
    companyKey?: string | undefined;
    search?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
    status?: string | undefined;
    direction?: string | undefined;
  }) =>
    (
      await api.get<{ appointments: PortalAppointment[]; total: number }>(
        '/customer/portal/appointments',
        { params },
      )
    ).data,
  request: async (
    key: string,
    input: {
      businessUnitId: string;
      serviceId: string;
      date: string;
      startTime: string;
      /// Distingue horários de mesmo início com fim diferente (ADR 0074).
      endTime?: string | undefined;
      professionalId?: string | undefined;
      prepaidBalanceId?: string | undefined;
      requestKey: string;
      policyVersion: string;
    },
  ) =>
    (
      await api.post<{ appointment: PortalAppointment }>(
        `/customer/portal/companies/${encodeURIComponent(key)}/requests`,
        input,
      )
    ).data.appointment,

  monthAvailability: async (
    key: string,
    params: {
      businessUnitId: string;
      serviceId: string;
      month: string;
      balanceId?: string | undefined;
      professionalId?: string | undefined;
    },
  ) =>
    (
      await api.get<{
        unavailabilityReason?: PortalAvailability['unavailabilityReason'];
        cancellationSummary?: PortalAvailability['cancellationSummary'];
        days: Array<{ date: string; slots: PortalAvailability['slots'] }>;
        professionals: PortalAvailability['professionals'];
        timeZone: string;
      }>(
        params.balanceId
          ? `/customer/portal/companies/${encodeURIComponent(key)}/prepaid-availability-month`
          : `/public/portal/companies/${encodeURIComponent(key)}/availability-month`,
        { params },
      )
    ).data,

  companies: async (params: { search: string; limit: number; offset: number }) =>
    (
      await api.get<{ companies: PortalCompany[]; total: number }>('/public/portal/companies', {
        params,
      })
    ).data,
  company: async (key: string) =>
    (
      await api.get<{ company: PortalCompany }>(
        `/public/portal/companies/${encodeURIComponent(key)}`,
      )
    ).data.company,
  availability: async (
    key: string,
    params: {
      businessUnitId: string;
      serviceId: string;
      date: string;
      professionalId?: string | undefined;
    },
  ) =>
    (
      await api.get<PortalAvailability>(
        `/public/portal/companies/${encodeURIComponent(key)}/availability`,
        { params },
      )
    ).data,
  publication: async () =>
    (await api.get<{ publication: PortalPublication }>('/tenant/scheduling/portal')).data
      .publication,
  save: async (publication: PortalPublication) => {
    await api.put('/tenant/scheduling/portal', publication);
  },
};
